<template>
  <div class="page-container">
    <Sidebar />
    
    <div class="main-content">
      <div class="page-header">
        <h1>知识库管理</h1>
        <button class="add-btn" @click="openAddModal">+ 添加文档</button>
      </div>
      
      <div class="filter-bar">
        <button :class="['filter-btn', filter === 'all' ? 'active' : '']" @click="filter = 'all'">全部</button>
        <button :class="['filter-btn', filter === 'tech' ? 'active' : '']" @click="filter = 'tech'">技术文档</button>
        <button :class="['filter-btn', filter === 'policy' ? 'active' : '']" @click="filter = 'policy'">政策法规</button>
      </div>
      
      <div class="content-body">
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>
        
        <div class="table-container" v-else>
          <table class="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>标题</th>
                <th>类别</th>
                <th>文件名</th>
                <th>下载次数</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in filteredList" :key="item.id">
                <td>{{ item.id }}</td>
                <td>{{ item.title }}</td>
                <td><span :class="['type-tag', item.category]">{{ getCategoryName(item.category) }}</span></td>
                <td>{{ item.file_name || '-' }}</td>
                <td>{{ item.download_count || 0 }}</td>
                <td><StatusTag :status="item.status" /></td>
                <td>{{ formatDateShort(item.created_at) }}</td>
                <td class="actions">
                  <button class="edit-btn" @click="editItem(item)">编辑</button>
                  <button class="delete-btn" @click="deleteItem(item.id)">删除</button>
                </td>
              </tr>
              <tr v-if="filteredList.length === 0">
                <td colspan="8" class="empty">暂无数据</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showModal">
      <div class="modal-content">
        <h2>{{ editId ? '编辑文档' : '添加文档' }}</h2>
        <form @submit.prevent="submitForm">
          <div class="form-group">
            <label>类别</label>
            <select v-model="form.category" required>
              <option value="tech">技术文档</option>
              <option value="policy">政策法规</option>
            </select>
          </div>
          <div class="form-group">
            <label>标题</label>
            <input type="text" v-model="form.title" required placeholder="输入文档标题" />
          </div>
          <div class="form-group">
            <label>描述</label>
            <textarea v-model="form.description" placeholder="输入文档描述（可选）"></textarea>
          </div>
          <div class="form-group">
            <label>文件URL</label>
            <input type="text" v-model="form.file_url" required placeholder="输入PDF文件URL" />
          </div>
          <div class="form-group">
            <label>文件名</label>
            <input type="text" v-model="form.file_name" placeholder="如：中药材生产技术规程.pdf" />
          </div>
          <div class="form-group">
            <label>状态</label>
            <select v-model="form.status">
              <option value="active">正常</option>
              <option value="disabled">禁用</option>
            </select>
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeModal">取消</button>
            <button type="submit" class="submit-btn">提交</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Sidebar from '../components/Sidebar.vue'
import StatusTag from '../components/StatusTag.vue'
import { formatDateShort } from '../utils/dateFormat'
import { getAdminPassword } from '../utils/api'

const API_BASE = '/api/admin'

export default {
  name: 'KnowledgeList',
  components: { Sidebar, StatusTag },
  data() {
    return {
      list: [],
      loading: true,
      error: '',
      filter: 'all',
      showModal: false,
      editId: null,
      form: { title: '', description: '', file_url: '', file_name: '', category: 'tech', status: 'active' }
    }
  },
  computed: {
    filteredList() {
      if (this.filter === 'all') return this.list
      return this.list.filter(item => item.category === this.filter)
    }
  },
  mounted() { this.fetchList() },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const response = await axios.get(`${API_BASE}/tech-documents`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          const data = response.data.data
          this.list = data.list || data || []
        }
      } catch (err) { this.error = '网络错误' }
      finally { this.loading = false }
    },
    getCategoryName(category) {
      const names = { tech: '技术文档', policy: '政策法规' }
      return names[category] || '技术文档'
    },
    openAddModal() {
      this.editId = null
      this.form = { title: '', description: '', file_url: '', file_name: '', category: 'tech', status: 'active' }
      this.showModal = true
    },
    editItem(item) {
      this.editId = item.id
      this.form = {
        title: item.title,
        description: item.description || '',
        file_url: item.file_url,
        file_name: item.file_name || '',
        category: item.category || 'tech',
        status: item.status || 'active'
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editId = null
      this.form = { title: '', description: '', file_url: '', file_name: '', category: 'tech', status: 'active' }
    },
    async submitForm() {
      try {
        const headers = { 'x-admin-password': getAdminPassword() }
        if (this.editId) await axios.put(`${API_BASE}/tech-documents/${this.editId}`, this.form, { headers })
        else await axios.post(`${API_BASE}/tech-documents`, this.form, { headers })
        this.closeModal()
        this.fetchList()
      } catch (err) { alert('操作失败') }
    },
    async deleteItem(id) {
      if (!confirm('确定删除该文档？')) return
      try {
        await axios.delete(`${API_BASE}/tech-documents/${id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        this.fetchList()
      } catch (err) { alert('删除失败') }
    }
  }
}
</script>

<style scoped>
.page-container { 
  display: flex; 
  min-height: 100vh; 
  background: #f5f5f5; 
}

.main-content { 
  flex: 1; 
  padding: 30px; 
  margin-left: 260px;
}

.page-header { 
  display: flex; 
  justify-content: space-between; 
  align-items: center; 
  margin-bottom: 20px; 
}

.page-header h1 { 
  font-size: 24px; 
  color: #2c3e50; 
}

.add-btn { 
  padding: 10px 20px; 
  background: #3498db; 
  color: white; 
  border: none; 
  border-radius: 5px; 
  cursor: pointer; 
}

.filter-bar { 
  display: flex; 
  gap: 10px; 
  margin-bottom: 20px; 
}

.filter-btn { 
  padding: 8px 20px; 
  background: white; 
  border: 1px solid #ddd; 
  border-radius: 5px; 
  cursor: pointer; 
}

.filter-btn.active { 
  background: #3498db; 
  color: white; 
  border-color: #3498db; 
}

.content-body { 
  background: white; 
  padding: 20px; 
  border-radius: 10px; 
  box-shadow: 0 2px 10px rgba(0,0,0,0.1); 
}

.loading, .error, .empty { 
  text-align: center; 
  padding: 50px; 
  color: #666; 
}

.table-container { 
  overflow-x: auto; 
}

.data-table { 
  width: 100%; 
  border-collapse: collapse; 
}

.data-table th, .data-table td { 
  padding: 12px 15px; 
  text-align: left; 
  border-bottom: 1px solid #eee; 
}

.data-table th { 
  background: #f8f9fa; 
  font-weight: bold; 
  color: #2c3e50; 
}

.data-table tr:hover { 
  background: #f5f5f5; 
}

.type-tag { 
  display: inline-block; 
  padding: 2px 8px; 
  border-radius: 3px; 
  font-size: 12px; 
}

.type-tag.tech { 
  background: #27ae60; 
  color: white; 
}

.type-tag.policy { 
  background: #9b59b6; 
  color: white; 
}

.status-tag { 
  display: inline-block; 
  padding: 2px 8px; 
  border-radius: 3px; 
  font-size: 12px; 
}

.status-tag.active { 
  background: #27ae60; 
  color: white; 
}

.status-tag.disabled { 
  background: #bdc3c7; 
  color: white; 
}

.actions { 
  display: flex; 
  gap: 10px; 
}

.edit-btn, .delete-btn { 
  padding: 6px 12px; 
  border: none; 
  border-radius: 5px; 
  cursor: pointer; 
  font-size: 12px; 
}

.edit-btn { 
  background: #3498db; 
  color: white; 
}

.delete-btn { 
  background: #e74c3c; 
  color: white; 
}

.modal { 
  position: fixed; 
  top: 0; 
  left: 0; 
  width: 100%; 
  height: 100%; 
  background: rgba(0,0,0,0.5); 
  display: flex; 
  justify-content: center; 
  align-items: center; 
  z-index: 1000; 
  margin-left: 260px;
}

.modal-content { 
  background: white; 
  padding: 30px; 
  border-radius: 10px; 
  width: 500px; 
  max-height: 90vh; 
  overflow-y: auto; 
}

.modal-content h2 { 
  margin: 0 0 20px; 
}

.form-group { 
  margin-bottom: 15px; 
}

.form-group label { 
  display: block; 
  margin-bottom: 5px; 
  color: #666; 
}

.form-group input, .form-group select, .form-group textarea { 
  width: 100%; 
  padding: 10px; 
  border: 1px solid #ddd; 
  border-radius: 5px; 
}

.form-group textarea { 
  min-height: 80px; 
  resize: vertical; 
}

.form-actions { 
  display: flex; 
  gap: 10px; 
  justify-content: flex-end; 
  margin-top: 20px; 
}

.cancel-btn, .submit-btn { 
  padding: 10px 20px; 
  border: none; 
  border-radius: 5px; 
  cursor: pointer; 
}

.cancel-btn { 
  background: #ddd; 
}

.submit-btn { 
  background: #3498db; 
  color: white; 
}
</style>
